import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'store/slices/AuthSlice';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_THRESHOLD_MS = 2 * 60 * 1000; // Show warning when 2 minutes remaining

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.value = 0.3;
    oscillator.start();
    // Two short beeps
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.3, ctx.currentTime + 0.25);
    gain.gain.setValueAtTime(0, ctx.currentTime + 0.4);
    oscillator.stop(ctx.currentTime + 0.5);
  } catch (e) {
    // Audio not supported or blocked by browser policy
  }
};

const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);

  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT_MS);
  const [isWarning, setIsWarning] = useState(false);
  const warningBeepPlayedRef = useRef(false);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setRemainingTime(SESSION_TIMEOUT_MS);
    setIsWarning(false);
    warningBeepPlayedRef.current = false;
  }, []);

  const handleLogout = useCallback(() => {
    // Clear timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Dispatch logout
    dispatch(logout());

    // Redirect to login
    window.location.href = '/';
  }, [dispatch]);

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isAuthenticated) {
      resetTimer();
    }
  }, [isAuthenticated, resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers if not authenticated
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Reset timer on mount
    resetTimer();

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttle activity handler to prevent excessive updates
    let throttleTimeout = null;
    const throttledHandler = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          handleActivity();
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledHandler, { passive: true });
    });

    // Update remaining time every second
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, SESSION_TIMEOUT_MS - elapsed);

      setRemainingTime(remaining);
      const nowWarning = remaining <= WARNING_THRESHOLD_MS && remaining > 0;
      setIsWarning(nowWarning);

      // Play beep sound once when warning starts
      if (nowWarning && !warningBeepPlayedRef.current) {
        warningBeepPlayedRef.current = true;
        playBeep();
      }

      // Auto logout when time expires
      if (remaining <= 0) {
        handleLogout();
      }
    }, 1000);

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledHandler);
      });
      if (throttleTimeout) clearTimeout(throttleTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, handleActivity, handleLogout, resetTimer]);

  // Format remaining time as MM:SS
  const formatTime = useCallback((ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    remainingTime,
    formattedTime: formatTime(remainingTime),
    isWarning,
    resetTimer,
    isAuthenticated
  };
};

export default useSessionTimeout;
